import {ActionIcon, Input, Popover} from '@mantine/core';
import {Calendar, TimeInput} from '@mantine/dates';
import {MdClose} from 'react-icons/md';
import {cn} from '../../../../../lib/classname';

// TimeInputのstep値（秒単位）: 600秒 = 10分間隔
const TIME_INPUT_STEP_SECONDS = 600;

/**
 * クリアボタンコンポーネント
 * 編集モードと表示モードの両方で使用される共通UI
 */
const ClearButton: React.FC<{
  onClick: (e: React.MouseEvent) => void;
}> = ({onClick}) => (
  <ActionIcon
    size="xs"
    variant="transparent"
    onClick={onClick}
    className="shrink-0 hover:opacity-60"
    title="クリア"
  >
    <MdClose size={14} />
  </ActionIcon>
);

/**
 * エラーメッセージコンポーネント
 * 編集モードと表示モードの両方で使用される共通UI
 */
const ErrorMessage: React.FC<{
  error?: string;
}> = ({error}) => {
  if (!error) return null;
  return <div className="text-xs text-error mt-4">{error}</div>;
};

/**
 * 状態関連のprops
 */
type StateProps = {
  isEditing: boolean;
  inputText: string;
  calendarOpened: boolean;
  calendarMonth: Date;
  timeValue: string;
  value: string | undefined;
  displayValue: string;
};

/**
 * 設定関連のprops
 */
type ConfigProps = {
  placeholder: string;
  disabled: boolean;
  withTime: boolean;
  /** 表示モード（ボタン）および編集モード側のInputラッパーに適用するクラス名。幅の指定などに使用 */
  displayClassName: string;
  className: string;
  error?: string;
  /** 'outlined': フォーム用（ボーダーあり）, 'inline': インライン編集用（ボーダーなし） */
  variant: 'outlined' | 'inline';
};

/**
 * イベントハンドラ関連のprops
 */
type HandlerProps = {
  inputRef: React.RefObject<HTMLInputElement>;
  onDisplayClick: () => void;
  onInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onInputBlur: () => void;
  onInputKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  onInputMouseDown: (e: React.MouseEvent) => void;
  onCalendarSelect: (date: Date) => void;
  onMonthChange: (date: Date) => void;
  onTimeChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onClear: (e: React.MouseEvent) => void;
  onPopoverClose: () => void;
};

export type ClickToEditDateInputPresenterProps = StateProps &
  ConfigProps &
  HandlerProps;

export const ClickToEditDateInputPresenter: React.FC<
  ClickToEditDateInputPresenterProps
> = ({
  isEditing,
  inputText,
  calendarOpened,
  calendarMonth,
  timeValue,
  value,
  displayValue,
  placeholder,
  disabled,
  withTime,
  displayClassName,
  className,
  error,
  variant,
  inputRef,
  onDisplayClick,
  onInputChange,
  onInputBlur,
  onInputKeyDown,
  onInputMouseDown,
  onCalendarSelect,
  onMonthChange,
  onTimeChange,
  onClear,
  onPopoverClose,
}) => {
  if (isEditing) {
    return (
      <div className={className}>
        <div className="flex items-center gap-4">
          <Popover
            opened={calendarOpened}
            onClose={onPopoverClose}
            position="bottom-start"
            withinPortal
            trapFocus={false}
            classNames={{
              dropdown: 'z-[1000]',
            }}
          >
            <Popover.Target>
              <Input
                ref={inputRef}
                type="text"
                value={inputText}
                onChange={onInputChange}
                onBlur={onInputBlur}
                onKeyDown={onInputKeyDown}
                onMouseDown={onInputMouseDown}
                placeholder={withTime ? 'yyyy/mm/dd hh:mm' : 'yyyy/mm/dd'}
                radius={variant === 'outlined' ? 24 : undefined}
                size={variant === 'outlined' ? undefined : 'xs'}
                classNames={{
                  input:
                    variant === 'outlined'
                      ? 'text-sm w-full'
                      : 'text-xs pc:text-sm w-full',
                  wrapper: cn('flex-1', displayClassName),
                }}
                error={!!error}
              />
            </Popover.Target>
            <Popover.Dropdown>
              <div className="flex flex-col gap-8">
                <Calendar
                  date={calendarMonth}
                  onDateChange={onMonthChange}
                  __onDayClick={(_, date) => onCalendarSelect(date)}
                  {...({} as any)}
                />
                {withTime && (
                  <div className="flex items-center gap-8 px-8 pb-8">
                    <span className="text-sm text-black-2">時刻:</span>
                    <TimeInput
                      value={timeValue}
                      onChange={onTimeChange}
                      size="xs"
                      step={TIME_INPUT_STEP_SECONDS}
                      classNames={{
                        input: 'text-sm w-[80px]',
                      }}
                    />
                  </div>
                )}
              </div>
            </Popover.Dropdown>
          </Popover>
          {value && !disabled && <ClearButton onClick={onClear} />}
        </div>
        <ErrorMessage error={error} />
      </div>
    );
  }

  return (
    <div className={className}>
      <div className="flex items-center gap-4">
        <button
          onClick={onDisplayClick}
          className={cn(
            'cursor-pointer flex items-center text-left flex-1',
            disabled && 'cursor-not-allowed opacity-50',
            variant === 'outlined' && [
              'hover:border-primary rounded-full border px-12 h-[36px] text-sm',
              error ? 'border-error' : 'border-line-1',
            ],
            variant === 'inline' && [
              'hover:bg-background-2 rounded px-8 py-4 min-h-[32px] text-xs pc:text-sm',
              error && 'border border-error',
            ],
            displayClassName,
          )}
          disabled={disabled}
          type="button"
        >
          {value ? (
            <span className="break-all">{displayValue}</span>
          ) : (
            <span className="text-black-3">{placeholder}</span>
          )}
        </button>
        {value && !disabled && <ClearButton onClick={onClear} />}
      </div>
      <ErrorMessage error={error} />
    </div>
  );
};
